/**
 * Execution Drawer Component
 *
 * Expandable bottom drawer showing detailed execution steps.
 * Uses independent selection state to avoid interfering with Canvas/NodeConfigPanel.
 */

import React, { useMemo, useCallback, useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  ChevronDown,
  History,
  Image,
  X,
} from 'lucide-react';
import {
  useWorkflowStore,
  useExecutionState,
  useIsExecuting,
} from '../hooks/useWorkflowStore';
import { StepResultDisplay } from './StepResultDisplay';
import { topologicalSort, getNodeKey } from '../utils';
import type { StepStatus } from '@/types/imageWorkflow';

// Status configuration
const statusConfig: Record<
  StepStatus,
  { label: string; color: string; bgColor: string; icon: React.ReactNode }
> = {
  pending: {
    label: 'Pending',
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
    icon: <Clock className="w-4 h-4" />,
  },
  running: {
    label: 'Running',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    icon: <Loader2 className="w-4 h-4 animate-spin" />,
  },
  completed: {
    label: 'Done',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    icon: <CheckCircle2 className="w-4 h-4" />,
  },
  failed: {
    label: 'Failed',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    icon: <XCircle className="w-4 h-4" />,
  },
  skipped: {
    label: 'Skipped',
    color: 'text-gray-400',
    bgColor: 'bg-gray-50',
    icon: <Clock className="w-4 h-4" />,
  },
};

interface ExecutionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenHistory: () => void;
}

export const ExecutionDrawer: React.FC<ExecutionDrawerProps> = ({
  isOpen,
  onClose,
  onOpenHistory,
}) => {
  // Independent selection state for drawer (doesn't affect Canvas/NodeConfigPanel)
  const [selectedNodeKey, setSelectedNodeKey] = useState<string | null>(null);

  const nodes = useWorkflowStore((s) => s.nodes);
  const edges = useWorkflowStore((s) => s.edges);
  const clearExecution = useWorkflowStore((s) => s.clearExecution);

  const execution = useExecutionState();
  const isExecuting = useIsExecuting();

  // Sort nodes by topological order
  const sortedNodes = useMemo(() => topologicalSort(nodes, edges), [nodes, edges]);

  // Calculate stats
  const totalNodes = sortedNodes.length;
  const completedNodes = Object.values(execution.nodeStatuses).filter(
    (s) => s === 'completed'
  ).length;
  const failedNodes = Object.values(execution.nodeStatuses).filter(
    (s) => s === 'failed'
  ).length;
  const progress = totalNodes > 0 ? (completedNodes / totalNodes) * 100 : 0;

  // Duration
  const getDuration = () => {
    if (!execution.startedAt) return null;
    const start = new Date(execution.startedAt).getTime();
    const end = execution.completedAt
      ? new Date(execution.completedAt).getTime()
      : Date.now();
    const seconds = Math.floor((end - start) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Handle node click (toggle selection)
  const handleNodeClick = useCallback((nodeKey: string) => {
    setSelectedNodeKey((prev) => (prev === nodeKey ? null : nodeKey));
  }, []);

  // Get selected node and result
  const selectedNode = sortedNodes.find((n) => getNodeKey(n) === selectedNodeKey);
  const selectedResult = selectedNodeKey ? execution.nodeResults[selectedNodeKey] : null;

  if (!isOpen) return null;

  const hasExecution = execution.executionId || execution.status;

  return (
    <div className="border-t bg-white shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
        <div className="flex items-center gap-4">
          <span className="font-medium text-gray-800">Execution Details</span>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>
              {completedNodes}/{totalNodes} completed
            </span>
            {failedNodes > 0 && (
              <span className="text-red-500">({failedNodes} failed)</span>
            )}
            {getDuration() && <span className="text-gray-400">| {getDuration()}</span>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isExecuting && hasExecution && (
            <button
              onClick={clearExecution}
              className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            >
              Clear
            </button>
          )}
          <button
            onClick={onOpenHistory}
            className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
          >
            <History className="w-4 h-4" />
            <span>History</span>
          </button>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 py-2 bg-gray-50">
        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              failedNodes > 0
                ? 'bg-red-500'
                : isExecuting
                ? 'bg-blue-500'
                : 'bg-green-500'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Error summary */}
      {execution.error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100">
          <div className="text-sm text-red-600">{execution.error}</div>
        </div>
      )}

      {/* Content area - responsive height */}
      <div className="flex max-h-[40vh] min-h-[200px] overflow-hidden">
        {/* Left: Node list */}
        <div className="w-80 border-r overflow-y-auto p-3 space-y-2">
          {sortedNodes.map((node, index) => {
            const nodeKey = getNodeKey(node);
            const status = execution.nodeStatuses[nodeKey] ?? 'pending';
            const result = execution.nodeResults[nodeKey];
            const config = statusConfig[status];
            const isSelected = selectedNodeKey === nodeKey;

            return (
              <button
                key={node.id}
                onClick={() => handleNodeClick(nodeKey)}
                className={`w-full text-left p-2 rounded-lg border transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`flex items-center justify-center w-5 h-5 rounded-full text-xs ${config.bgColor} ${config.color}`}
                  >
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-800 truncate">
                      {node.data.label}
                    </div>
                  </div>
                  <span className={config.color}>{config.icon}</span>
                </div>

                {/* Preview */}
                {status === 'completed' && result?.output_data && (
                  <div className="mt-1 ml-7 text-xs text-gray-500">
                    {!!(result.output_data as Record<string, unknown>).image_url && (
                      <span className="flex items-center gap-1">
                        <Image className="w-3 h-3" /> Image generated
                      </span>
                    )}
                    {!!(result.output_data as Record<string, unknown>).prompt && (
                      <span className="truncate block">
                        {String((result.output_data as Record<string, unknown>).prompt).slice(0, 40)}...
                      </span>
                    )}
                  </div>
                )}

                {status === 'failed' && result?.error_message && (
                  <div className="mt-1 ml-7 text-xs text-red-500 truncate">
                    {result.error_message}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Right: Selected node detail */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {selectedResult ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-800">
                  {selectedNode?.data.label} - Result
                </h4>
                <button
                  onClick={() => setSelectedNodeKey(null)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <StepResultDisplay step={selectedResult} />
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Select a node to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExecutionDrawer;
